/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import { saveAs } from 'file-saver';

import { apiGet } from '../../utils/apiClient';
import { useTranslation } from '../../i18n';
import {
  Response,
  ServiceType,
  Subtype,
} from '../../types/TrialBookingTable.type';
import { serviceTypeColors, trialStatusColors } from '../../utils/colorHandler';
import { notifyAPIClient } from '../../utils/toast';
import ColorChip from '../../components/common/ColorChip';
import TableLoader from '../../components/common/TableLoader';
import { EmptyValue, isParentEmpty } from '../../components/common/EmptyValue';
import { rowHoverBg, selectedRowBg } from '../../utils/rowHighlight';

interface TrialReportItem {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  parent: string | null;
  dob: string;
  subtypeId: number;
  subtype: {
    id: number;
    name: string;
  };
  trialDate: string;
  note: string;
  result: 'ENROLL' | 'PENDING' | 'NOT_COME' | 'CONSIDER' | 'ALL';
  createdAt: string;
  updateAt: string;
  deletionMark: boolean;
}

interface TrialReportResponse {
  code: number;
  message: string;
  data: TrialReportItem[];
  metadata: {
    totalItems: number;
    currentPage: number;
    totalPages: number;
    limit: number;
    totalTrials: number;
    enrollTrials: number;
    nonEnrollTrials: number;
    enrollRate: number;
  };
}

// Aquatic blue — the app's primary accent (matches table headers, etc.).
const ENROLL_COLOR = '#4da7db';
const NON_ENROLL_COLOR = '#dfe7ec';

const TrialTable: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);

  const [trials, setTrials] = useState<TrialReportItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [subTypeFilter, setSubTypeFilter] = useState<number>(-1);
  const [resultFilter, setResultFilter] = useState('ALL');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [subtypeOptions, setSubtypeOptions] = useState<Subtype[]>([]);

  const [totalTrials, setTotalTrials] = useState(0);
  const [enrollTrials, setEnrollTrials] = useState(0);
  const [enrollRate, setEnrollRate] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  const fetchSubtypeOptions = async () => {
    try {
      const res = await apiGet<Response<ServiceType>>(
        '/service/filter?name=COURSE',
      );
      setSubtypeOptions(res.data[0]?.subtypes || []);
    } catch (error) {
      console.error('Failed to fetch subtype options:', error);
      setSubtypeOptions([]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const startDateParam =
        startDate && startDate.isValid()
          ? `&startDate=${startDate.format('YYYY-MM-DD')}`
          : '';
      const endDateParam =
        endDate && endDate.isValid()
          ? `&endDate=${endDate.format('YYYY-MM-DD')}`
          : '';

      const response = await apiGet<TrialReportResponse>(
        `/report/get-trial-report?page=${page + 1}&limit=${rowsPerPage}&search=${searchTerm}&subtypeId=${subTypeFilter}&result=${resultFilter}${startDateParam}${endDateParam}`,
      );

      setTrials(response.data);
      setTotalItems(response.metadata.totalItems);
      setTotalTrials(response.metadata.totalTrials);
      setEnrollTrials(response.metadata.enrollTrials);
      setEnrollRate(response.metadata.enrollRate);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubtypeOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, subTypeFilter, resultFilter, startDate, endDate]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatResultString = (result: string) => result.replace(/_/g, ' ');

  const handleExportTrialReport = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      const trimmedSearch = searchTerm.trim();

      if (trimmedSearch) {
        queryParams.append('search', trimmedSearch);
      }

      queryParams.append('subtypeId', `${subTypeFilter ?? -1}`);
      queryParams.append('result', resultFilter || 'ALL');

      // Export endpoint supports a single date filter, so we prioritize startDate.
      const exportDate = startDate?.isValid()
        ? startDate
        : endDate?.isValid()
          ? endDate
          : null;

      if (exportDate) {
        queryParams.append('date', exportDate.format('YYYY-MM-DD'));
      }

      const response = await apiGet<Blob>(
        `/report/export-trial-report?${queryParams.toString()}`,
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([response as unknown as BlobPart], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const fileDateLabel = exportDate
        ? exportDate.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');

      saveAs(blob, `trial_booking_list_${fileDateLabel}.xlsx`);
    } catch (error) {
      console.error('Failed to export trial report:', error);
      notifyAPIClient(t('report.trial_exportFailed'));
    } finally {
      setExportLoading(false);
    }
  };

  const resultLabelMap: Record<string, string> = {
    ENROLL: t('report.trial_enroll'),
    PENDING: t('report.trial_pending'),
    NOT_COME: t('report.trial_notCome'),
    CONSIDER: t('report.trial_consider'),
  };

  const pieData = useMemo(
    () => [
      { name: t('report.trial_enroll'), value: enrollTrials },
      {
        name: t('report.trial_notEnroll'),
        value: Math.max(totalTrials - enrollTrials, 0),
      },
    ],
    [enrollTrials, totalTrials, t],
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          lg: 'minmax(0, 2.5fr) minmax(280px, 1fr)',
        },
        gap: 3,
        alignItems: 'stretch',
      }}
    >
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {t('report.trial_bookingReport')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {t('report.trial_totalTrialsLabel')}{' '}
              <span style={{ fontWeight: 'bold', color: ENROLL_COLOR }}>
                {totalTrials}
              </span>
              <span style={{ color: '#999', marginLeft: '8px' }}>•</span>
              <span
                style={{
                  fontWeight: 'bold',
                  color: ENROLL_COLOR,
                  marginLeft: '8px',
                }}
              >
                {t('report.trial_percentEnrolled', { rate: enrollRate })}
              </span>
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2 },
              flexWrap: 'wrap',
              marginLeft: 'auto',
              justifyContent: 'flex-end',
              width: {
                xs: '100%',
                sm: 'auto',
              },
              maxWidth: '100%',
            }}
          >
            <TextField
              size="small"
              placeholder={t('report.trial_searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  setPage(0);
                  fetchData();
                }
              }}
              sx={{
                width: {
                  xs: '100%',
                  sm: 240,
                  md: 260,
                  lg: 220,
                  xl: 260,
                },
                flex: {
                  xs: '1 1 100%',
                  sm: '0 1 auto',
                  lg: '0 0 auto',
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <FormControl
              size="small"
              sx={{
                minWidth: {
                  xs: 'calc(50% - 6px)',
                  sm: 140,
                  lg: 130,
                  xl: 150,
                },
                flex: {
                  xs: '1 1 calc(50% - 6px)',
                  sm: '0 0 auto',
                },
              }}
            >
              <Select
                displayEmpty
                value={String(subTypeFilter)}
                onChange={(event) => {
                  setSubTypeFilter(Number(event.target.value));
                  setPage(0);
                }}
              >
                <MenuItem value="-1">{t('report.trial_allServices')}</MenuItem>
                {subtypeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{
                minWidth: {
                  xs: 'calc(50% - 6px)',
                  sm: 140,
                  lg: 130,
                  xl: 150,
                },
                flex: {
                  xs: '1 1 calc(50% - 6px)',
                  sm: '0 0 auto',
                },
              }}
            >
              <Select
                displayEmpty
                value={resultFilter}
                onChange={(event) => {
                  setResultFilter(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">{t('report.trial_allResult')}</MenuItem>
                <MenuItem value="ENROLL">{t('report.trial_enroll')}</MenuItem>
                <MenuItem value="PENDING">{t('report.trial_pending')}</MenuItem>
                <MenuItem value="NOT_COME">{t('report.trial_notCome')}</MenuItem>
                <MenuItem value="CONSIDER">
                  {t('report.trial_consider')}
                </MenuItem>
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  width: {
                    xs: 'calc(50% - 6px)',
                    sm: 160,
                    lg: 145,
                    xl: 165,
                  },
                  flex: {
                    xs: '1 1 calc(50% - 6px)',
                    sm: '0 0 auto',
                  },
                }}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  label={t('report.trial_startDate')}
                  value={startDate}
                  onChange={(newDate) => {
                    setStartDate(newDate);
                    setPage(0);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    },
                    field: {
                      clearable: true,
                      onClear: () => setStartDate(null),
                    },
                  }}
                  maxDate={endDate || undefined}
                />
              </Box>
              <Box
                sx={{
                  width: {
                    xs: 'calc(50% - 6px)',
                    sm: 160,
                    lg: 145,
                    xl: 165,
                  },
                  flex: {
                    xs: '1 1 calc(50% - 6px)',
                    sm: '0 0 auto',
                  },
                }}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  label={t('report.trial_endDate')}
                  value={endDate}
                  onChange={(newDate) => {
                    setEndDate(newDate);
                    setPage(0);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    },
                    field: {
                      clearable: true,
                      onClear: () => setEndDate(null),
                    },
                  }}
                  minDate={startDate || undefined}
                />
              </Box>
            </LocalizationProvider>

            <Tooltip title={t('report.trial_exportTooltip')}>
              <IconButton
                onClick={handleExportTrialReport}
                loading={exportLoading}
                disabled={totalItems === 0 || exportLoading}
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer sx={{ flexGrow: 1, overflowX: 'auto' }}>
          <Table size="small" padding="checkbox">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#4da7db' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('report.trial_fullName')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('report.trial_email')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('report.trial_phone')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('report.trial_parent')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('report.trial_program')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('report.trial_trialDate')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('report.trial_result')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('report.trial_note')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableLoader colSpan={8} />
              ) : (
                <>
              {trials.map((trial) => (
                <TableRow
                  key={trial.id}
                  sx={{
                    '&:hover': { backgroundColor: rowHoverBg },
                    backgroundColor:
                      highlightedRow === `data_${trial.id}`
                        ? selectedRowBg
                        : 'inherit',
                  }}
                  onClick={() => {
                    setHighlightedRow((prev) =>
                      prev === `data_${trial.id}` ? null : `data_${trial.id}`,
                    );
                  }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {trial.fullName || `${trial.lastName} ${trial.firstName}`}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {trial.email || <EmptyValue />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {trial.phone || <EmptyValue />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {isParentEmpty(trial.parent) ? (
                      <EmptyValue />
                    ) : (
                      trial.parent
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip
                      label={trial.subtype?.name}
                      palette={serviceTypeColors}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {dayjs(trial.trialDate).format('DD/MM/YYYY - HH:mm')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip
                      label={
                        trial.result
                          ? (resultLabelMap[trial.result] ??
                            formatResultString(trial.result))
                          : null
                      }
                      palette={trialStatusColors}
                      paletteKey={trial.result || undefined}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {trial.note || <EmptyValue />}
                  </TableCell>
                </TableRow>
              ))}

              {trials.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('report.trial_noRecords')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          height: '100%',
        }}
      >
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: '100%',
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t('report.trial_enrollRatio')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('report.trial_enrolledSummary', {
              enrolled: enrollTrials,
              total: totalTrials,
              rate: enrollRate,
            })}
          </Typography>
          <Box
            sx={{
              width: '100%',
              minHeight: {
                xs: 260,
                sm: 300,
                md: 340,
                lg: 320,
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 1,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={92}
                  paddingAngle={2}
                  dataKey="value"
                >
                  <Cell fill={ENROLL_COLOR} />
                  <Cell fill={NON_ENROLL_COLOR} />
                </Pie>
                <RechartsTooltip
                  formatter={(value: number) => [
                    t('report.trial_trialsCount', { count: value }),
                    t('report.trial_count'),
                  ]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default TrialTable;
